import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CourseManagementAdmin = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCourseStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            console.log('🔍 Fetching admin course stats...');
            const response = await axios.get('/api/admin/course-stats', { timeout: 10000 });
            setCourses(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('❌ Course stats error:', err);
            setError('Failed to load course statistics');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCourseStats();
    }, [fetchCourseStats]);

    // Variables for summary stats
    const totalCourses = courses.length;
    const totalActive = courses.reduce((acc, curr) => acc + (Number(curr.currently_doing) || 0), 0);
    const totalEnrolled = courses.reduce((acc, curr) => acc + (Number(curr.total_enrolled) || 0), 0);

    if (loading) return <div style={{padding: '50px', textAlign: 'center', fontSize: '18px', fontFamily: 'system-ui'}}>📊 Loading Course Stats...</div>;
    if (error) return <div style={{padding: '50px', textAlign: 'center', color: 'red', fontSize: '16px', fontFamily: 'system-ui'}}>⚠️ {error}</div>;

    return (
        <div style={containerStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <div style={headerLeftStyle}>
                    <span style={iconStyle}>📚</span>
                    <div>
                        <h1 style={pageTitleStyle}>Admin Course Management</h1>
                        <p style={pageSubtitleStyle}>{totalCourses} courses loaded</p>
                    </div>
                </div>
                <button onClick={fetchCourseStats} style={refreshBtnStyle}>🔄 Refresh</button>
            </div>

            {/* Stats Cards */}
            <div style={statsRowStyle}>
                <div style={statCardStyle('#3b82f6')}>
                    <span style={statIconStyle}>📚</span>
                    <div>
                        <div style={statLabelStyle}>Total Courses</div>
                        <div style={statValueStyle}>{totalCourses}</div>
                    </div>
                </div>
                <div style={statCardStyle('#10b981')}>
                    <span style={statIconStyle}>👥</span>
                    <div>
                        <div style={statLabelStyle}>Active Students</div>
                        <div style={statValueStyle}>{totalActive}</div>
                    </div>
                </div>
                <div style={statCardStyle('#f59e0b')}>
                    <span style={statIconStyle}>🎯</span>
                    <div>
                        <div style={statLabelStyle}>Total Enrollments</div>
                        <div style={statValueStyle}>{totalEnrolled}</div>
                    </div>
                </div>
            </div>

            {/* Table Card */}
            <div style={tableCardStyle}>
                <div style={tableHeaderStyle}>
                    <h3 style={cardTitleStyle}>Course Enrollment Details ({totalCourses})</h3>
                </div>
                <div style={{overflowX: 'auto'}}>
                    <table style={tableStyle}>
                        <thead style={theadStyle}>
                            <tr>
                                <th style={thStyle}>Code</th>
                                <th style={thStyle}>Course</th>
                                <th style={thStyle}>Faculty</th>
                                <th style={thStyle}>Total</th>
                                <th style={thStyle}>Active</th>
                                <th style={thStyle}>Dropped</th>
                            </tr>
                        </thead>
                        <tbody>
                            {courses.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={emptyCellStyle}>
                                        <div style={{textAlign: 'center', padding: '60px'}}>
                                            <span style={{fontSize: '48px', display: 'block', marginBottom: '16px'}}>📚</span>
                                            <div style={{fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px'}}>No Courses Found</div>
                                            <div style={{color: '#64748b'}}>No course statistics available</div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                courses.map((course, index) => (
                                    <tr key={course.course_id || index} style={trStyle}>
                                        <td style={tdStyle}>
                                            <span style={courseCodeStyle}>{course.course_code}</span>
                                        </td>
                                        <td style={tdStyle}>{course.course_name}</td>
                                        <td style={tdStyle}>
                                            <span style={facultyStyle}>👤 {course.provider_name || 'N/A'}</span>
                                        </td>
                                        <td style={tdNumberStyle}>
                                            <span style={badgeStyle('#3b82f6')}>{course.total_enrolled || 0}</span>
                                        </td>
                                        <td style={tdNumberStyle}>
                                            <span style={badgeStyle('#10b981')}>{course.currently_doing || 0}</span>
                                        </td>
                                        <td style={tdNumberStyle}>
                                            <span style={badgeStyle('#ef4444')}>{course.dropped_students || 0}</span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const containerStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px',
    background: '#f8fafc',
    fontFamily: 'system-ui, -apple-system, sans-serif'
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    padding: '24px 0'
};

const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
};

const iconStyle = {
    fontSize: '28px'
};

const pageTitleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0'
};

const pageSubtitleStyle = {
    fontSize: '16px',
    color: '#64748b',
    margin: 0
};

const refreshBtnStyle = {
    padding: '8px 16px',
    background: '#f8fafc',
    color: '#1e3a8a',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer'
};

const statsRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    marginBottom: '32px'
};

const statCardStyle = (color) => ({
    background: '#fff',
    padding: '24px',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    borderLeft: `4px solid ${color}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
});

const statIconStyle = {
    fontSize: '24px'
};

const statLabelStyle = {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '500',
    marginBottom: '4px'
};

const statValueStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b'
};

const tableCardStyle = {
    background: '#fff',
    borderRadius: '20px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden'
};

const tableHeaderStyle = {
    padding: '28px 32px',
    borderBottom: '1px solid #f1f5f9'
};

const cardTitleStyle = {
    fontSize: '22px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
};

const theadStyle = {
    background: '#f8fafc'
};

const thStyle = {
    textAlign: 'left',
    padding: '20px 24px',
    color: '#64748b',
    fontSize: '13px',
    fontWeight: '600',
    textTransform: 'uppercase',
    borderBottom: '2px solid #f1f5f9'
};

const trStyle = {
    background: '#fff'
};

const tdStyle = {
    padding: '20px 24px',
    fontSize: '15px',
    color: '#1e293b',
    verticalAlign: 'middle',
    borderBottom: '1px solid #f8fafc'
};

const tdNumberStyle = {
    padding: '20px 24px',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderBottom: '1px solid #f8fafc'
};

const courseCodeStyle = {
    fontWeight: '600',
    background: '#eff6ff',
    color: '#1e3a8a',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '14px'
};

const facultyStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#64748b'
};

const badgeStyle = (color) => ({
    padding: '8px 16px',
    borderRadius: '20px',
    fontWeight: '600',
    fontSize: '14px',
    border: `2px solid ${color}`,
    backgroundColor: `${color}20`,
    color: color,
    display: 'inline-block',
    minWidth: '60px'
});

const emptyCellStyle = {
    padding: 0,
    textAlign: 'left'
};

export default CourseManagementAdmin;